import { ShaderMaterial, Vector4 } from "three";
import { roleUniforms } from "../style/roles.ts";
import type { AtmosphereUniforms } from "../atmosphere.ts";
import { assemble } from "./chunks.ts";
import { SURF_PARAMS } from "./surf.ts";

const vertexShader = /* glsl */ `
in vec4 color;
// Cor interpolada: com \`flat\`, a divisa entre biomas (capim > rocha) virava serrilhado de triângulos.
out vec3 vColor;
out vec3 vWorldPos;
out vec3 vNormal;

void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorldPos = world.xyz;
  // Normal suave do worker (Fase 13): os morros rolam; as chunks não têm rotação nem escala.
  vNormal = normal;
  // Cores de bioma vêm em sRGB 0–1; o shader trabalha em espaço linear.
  vColor = pow(color.rgb, vec3(2.2));
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const fragmentShader = /* glsl */ `
${assemble("fragment", ["atmo", "keyFill", "blobFragment", "fogGlow", "styleCore", "energy", "ringLight", "sunShadow", "surf"])}
in vec3 vColor;
in vec3 vWorldPos;
in vec3 vNormal;

void main() {
  // A luz usa a normal suave, com uma pitada da face (2 m) para o chão não virar plástico.
  vec3 facet = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
  if (facet.y < 0.0) facet = -facet;
  // Sem o atributo (geometrias de vitrine), a face.
  vec3 smoothN = dot(vNormal, vNormal) > 0.01 ? normalize(vNormal) : facet;
  vec3 normal = normalize(mix(smoothN, facet, 0.2));
  float ndl = dot(normal, uLightDir);
  float w = rampWidth(ndl);
  vec3 albedo = vColor;

  // Rocha nas encostas íngremes, em qualquer bioma, pela normal suave: pela face, cada triângulo era
  // capim ou rocha inteiro e a divisa saía em dente de serra.
  float rock = 1.0 - smoothstep(0.48, 0.62, smoothN.y);
  albedo = mix(albedo, vec3(0.25, 0.235, 0.21), rock * 0.8);

  // Areia molhada junto à linha d'água: uma faixa úmida fixa e, acima dela, a marca do espraiado (Fase
  // 15), que a onda molha e que seca até a próxima. Terreno submerso escurece no matiz da água.
  float depth = uSeaLevel - vWorldPos.y;
  float above = -depth;
  float wetLine = surfWetLine(vWorldPos.xz);
  float wet = max((1.0 - smoothstep(-0.1, 0.35, above)) * 0.6, 1.0 - smoothstep(wetLine - 0.04, wetLine + 0.01, above));
  wet *= 1.0 - smoothstep(0.6, 1.0, above);
  albedo *= mix(1.0, 0.72, wet);
  if (depth > 0.0) albedo = mix(albedo, albedo * 0.72 * seaHue(), clamp(depth / 8.0, 0.0, 1.0));

  vec3 ambient = toonAmbient(normal, ndl);
  // Sombra do sol (G3): o sol some na sombra; o ambiente fica (frio, nunca preto).
  vec3 color = albedo * (ambient + toonKey(ndl, w) * sunShadow(vWorldPos, smoothN));
  // Sombras-blob de jogadores e criaturas (F51): só o ambiente, sem o sol.
  color = blobShade(color, albedo * ambient, blobShadow(vWorldPos));
  color = styleCompose(color, albedo, normal, normalize(cameraPosition - vWorldPos), 0.0);
  // Resposta local de combate (pesquisa 11): habilidades e impactos dão um banho fraco no chão vizinho,
  // na cor do dono, por pouco tempo.
  color += energyGlow(vWorldPos) * 0.18;
  // O anel do jogador acende o chão em volta.
  color += ringLight(vWorldPos) * albedo * 1.5;
  color = applyFog(color, vWorldPos, cameraPosition);
  gl_FragColor = vec4(color, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export function createTerrainMaterial(atmosphere: AtmosphereUniforms): ShaderMaterial {
  return new ShaderMaterial({
    name: "ilha/terreno",
    // Papel `terreno` (F59).
    uniforms: { ...atmosphere, ...roleUniforms("terreno"), uSurf: { value: new Vector4(...SURF_PARAMS) } },
    vertexShader,
    fragmentShader,
  });
}
